import React, { useEffect, useState } from "react";
import api, { downloadFile, openFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Button, Empty, Badge, Modal, Field, Input, Select } from "@/components/ui-kit";
import { CommentsThread } from "@/components/CommentsThread";
import { fmtDate, fmtDateTime } from "@/lib/utils";
import { PiggyBank, CalendarPlus, ShoppingBag, Plus, Pencil, Trash2, TrendingUp, TrendingDown, Wallet, ListTodo, FileText, FolderOpen, Vote, Upload, Download, Users, Check, CalendarClock, MapPin, Send, X, Clock, GripVertical, ThumbsUp, ChevronDown, MessageSquare, BadgeCheck, ExternalLink, Lock, ShieldCheck, Coins, CreditCard } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";

const TABS = [
  { k: "tresorerie", label: "Trésorerie", Icon: PiggyBank },
  { k: "caisse", label: "Caisse", Icon: Coins },
  { k: "reunions", label: "Réunions", Icon: CalendarClock },
  { k: "events", label: "Événements & Tâches", Icon: CalendarPlus },
  { k: "boutique", label: "Boutique", Icon: ShoppingBag },
  { k: "documents", label: "Documents (PV)", Icon: FolderOpen },
  { k: "votes", label: "Votes", Icon: Vote },
  { k: "membres", label: "Membres", Icon: Users },
];
const STATUS = { todo: { label: "À faire", color: "gray" }, in_progress: { label: "En cours", color: "yellow" }, done: { label: "Terminé", color: "green" } };
const euro = (n) => `${(n || 0).toFixed(2)} €`;

export default function Committee() {
  const [tab, setTab] = useState("tresorerie");
  return (
    <div data-testid="committee-page">
      <PageHeader title="Comité des parents" subtitle="Trésorerie, événements, boutique, documents et votes du comité." />
      <div className="flex gap-2 mb-6 flex-wrap">
        {TABS.map((t) => (
          <button key={t.k} data-testid={`committee-tab-${t.k}`} onClick={() => setTab(t.k)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-colors ${tab === t.k ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
            <t.Icon size={16} /> {t.label}
          </button>
        ))}
      </div>
      {tab === "tresorerie" && <Treasury />}
      {tab === "caisse" && <Caisse />}
      {tab === "reunions" && <Meetings onGoToEvents={() => setTab("events")} />}
      {tab === "events" && <Events />}
      {tab === "boutique" && <Shop />}
      {tab === "documents" && <Documents />}
      {tab === "votes" && <Votes />}
      {tab === "membres" && <MembersAdmin />}
    </div>
  );
}

/* ---------------- Trésorerie ---------------- */
function Treasury() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canConfirm = isAdmin || !!user?.committee_manager;
  const [data, setData] = useState({ entries: [], summary: {} });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [audit, setAudit] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const empty = { type: "recette", method: "banque", pending: false, amount: "", label: "", category: "", date: new Date().toISOString().slice(0, 10) };

  const loadAudit = () => { if (isAdmin) api.get("/committee/ledger/audit").then((r) => setAudit(r.data)).catch(() => {}); };
  const load = () => api.get("/committee/ledger").then((r) => setData(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadAudit(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (form.id) await api.put(`/committee/ledger/${form.id}`, form);
      else await api.post("/committee/ledger", form);
      toast.success("Enregistré"); setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer cette entrée ?")) return;
    try { await api.delete(`/committee/ledger/${id}`); toast.success("Supprimé"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const toggleConfirm = async (e) => {
    try {
      await api.patch(`/committee/ledger/${e.id}/confirm`, { confirmed: !e.confirmed });
      toast.success(e.confirmed ? "Entrée déverrouillée" : "Entrée confirmée ✔"); load(); loadAudit();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const s = data.summary || {};
  const num = (v) => v || 0;
  const tiles = [
    { key: "recettes_banque", label: "Recettes Banque", value: num(s.recettes_banque), tone: "green" },
    { key: "recettes_cash", label: "Recettes Cash", value: num(s.recettes_cash), tone: "green" },
    { key: "depenses_banque", label: "Dépenses Banque", value: num(s.depenses_banque), tone: "red" },
    { key: "depenses_cash", label: "Dépenses Cash", value: num(s.depenses_cash), tone: "red" },
    { key: "solde_banque", label: "Solde Banque", value: num(s.solde_banque), tone: "solde" },
    { key: "solde_cash", label: "Solde Cash", value: num(s.solde_cash), tone: "solde" },
    { key: "attente_banque", label: "En attente Banque", value: num(s.attente_banque), tone: "attente" },
    { key: "attente_cash", label: "En attente Cash", value: num(s.attente_cash), tone: "attente" },
  ];

  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
        {tiles.map((t) => (
          <Card key={t.key} data-testid={`treasury-${t.key}`}>
            <div className="flex items-center gap-2 text-muted-foreground text-[11px] uppercase tracking-widest font-bold">
              {t.tone === "green" && <TrendingUp size={13} className="text-green-500" />}
              {t.tone === "red" && <TrendingDown size={13} className="text-destructive" />}
              {t.tone === "solde" && <Wallet size={13} />}
              {t.tone === "attente" && <Clock size={13} className="text-orange-400" />}
              {t.label}
            </div>
            <p className={`text-xl sm:text-2xl font-bold mt-2 ${t.tone === "green" ? "text-green-500" : t.tone === "red" ? "text-destructive" : t.tone === "attente" ? "text-orange-400" : t.value >= 0 ? "text-primary" : "text-destructive"}`}>{euro(t.value)}</p>
          </Card>
        ))}
      </div>
      <Card data-testid="treasury-solde_total" className="border-primary/40 mb-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest font-bold"><Wallet size={14} /> Solde Total (Banque + Cash)</div>
          <p className={`text-3xl font-black ${num(s.solde_total) >= 0 ? "text-primary" : "text-destructive"}`}>{euro(num(s.solde_total))}</p>
        </div>
      </Card>

      <div className="flex justify-end gap-2 mb-4">
        <Button data-testid="export-ledger-pdf" variant="outline"
          onClick={() => downloadFile("/committee/ledger.pdf", "livre_comptes_comite.pdf").catch(() => toast.error("Export échoué"))}>
          <FileText size={16} /> PDF
        </Button>
        <Button data-testid="add-ledger-btn" onClick={() => { setForm(empty); setOpen(true); }}><Plus size={16} /> Nouvelle entrée</Button>
      </div>

      {data.entries.length ? (
        <div className="space-y-2">
          {data.entries.map((e) => (
            <Card key={e.id} data-testid="ledger-row" className="py-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge color={e.type === "recette" ? "green" : "red"}>{e.type === "recette" ? "Recette" : "Dépense"}</Badge>
                    {e.pending
                      ? <Badge color="orange">{e.method === "cash" ? "En attente cash" : "En attente banque"}</Badge>
                      : <Badge color={e.method === "cash" ? "yellow" : "blue"}>{e.method === "cash" ? "Cash" : "Banque"}</Badge>}
                    {e.confirmed && <Badge color="green"><span className="flex items-center gap-1"><BadgeCheck size={12} /> Confirmé</span></Badge>}
                    <span className="font-semibold text-sm truncate">{e.label || "—"}</span>
                    {e.category && <span className="text-xs text-muted-foreground">· {e.category}</span>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{fmtDate(e.date)}{e.confirmed && e.confirmed_by ? ` · confirmé par ${e.confirmed_by}` : ""}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`font-bold ${e.type === "recette" ? "text-green-500" : "text-destructive"}`}>{e.type === "recette" ? "+" : "−"}{euro(e.amount)}</span>
                  {canConfirm && (
                    <button data-testid={`confirm-ledger-${e.id}`} onClick={() => toggleConfirm(e)} title={e.confirmed ? "Déverrouiller (admin/manager)" : "Confirmer l'entrée"}
                      className={e.confirmed ? "text-green-500 hover:text-muted-foreground" : "text-muted-foreground hover:text-green-500"}>
                      {e.confirmed ? <Lock size={16} /> : <BadgeCheck size={16} />}
                    </button>
                  )}
                  {(!e.confirmed || canConfirm) ? (
                    <>
                      <button data-testid={`edit-ledger-${e.id}`} onClick={() => { setForm({ ...e, method: e.method || "banque", pending: !!e.pending, date: (e.date || "").slice(0, 10) }); setOpen(true); }} className="text-muted-foreground hover:text-foreground"><Pencil size={16} /></button>
                      <button data-testid={`delete-ledger-${e.id}`} onClick={() => remove(e.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={16} /></button>
                    </>
                  ) : (
                    <span data-testid={`locked-ledger-${e.id}`} title="Verrouillé : seul un administrateur ou un manager peut modifier" className="text-muted-foreground"><Lock size={16} /></span>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : <Empty text="Aucune entrée. Ajoutez une recette ou une dépense." />}

      {isAdmin && (
        <Card className="mt-6" data-testid="managers-journal">
          <button data-testid="toggle-managers-journal" onClick={() => setShowAudit((v) => !v)}
            className="w-full flex items-center justify-between gap-2 text-left">
            <span className="flex items-center gap-2 font-bold text-sm"><ListTodo size={16} className="text-primary" /> Journal des managers <span className="text-xs text-muted-foreground font-normal">({audit.length})</span></span>
            <ChevronDown size={18} className={`text-muted-foreground transition-transform ${showAudit ? "rotate-180" : ""}`} />
          </button>
          {showAudit && (
            <div className="mt-3 border-t border-border pt-3">
              {audit.length ? (
                <div className="space-y-2">
                  {audit.map((a) => (
                    <div key={a.id} data-testid="audit-row" className="flex items-center justify-between gap-3 text-sm rounded-lg bg-secondary/30 px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        {a.action === "confirm"
                          ? <Badge color="green"><span className="flex items-center gap-1"><BadgeCheck size={12} /> Confirmé</span></Badge>
                          : <Badge color="orange"><span className="flex items-center gap-1"><Lock size={12} /> Déverrouillé</span></Badge>}
                        <span className="truncate">
                          <b className="text-foreground">{a.actor}</b>
                          {a.is_admin ? " (admin)" : " (manager)"} · {a.entry_label || "—"}
                          <span className="text-muted-foreground"> · {euro(a.amount)}</span>
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground shrink-0">{fmtDateTime(a.at)}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-xs text-muted-foreground">Aucune action de confirmation pour l'instant.</p>}
            </div>
          )}
        </Card>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={form?.id ? "Modifier l'entrée" : "Nouvelle entrée"}>
        {form && (
          <form onSubmit={save}>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Type">
                <Select data-testid="ledger-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  <option value="recette">Recette</option>
                  <option value="depense">Dépense</option>
                </Select>
              </Field>
              <Field label="Mode de paiement">
                <Select data-testid="ledger-method" value={form.pending ? `attente_${form.method}` : form.method}
                  onChange={(e) => {
                    const v = e.target.value;
                    const pending = v.startsWith("attente_");
                    const method = pending ? v.replace("attente_", "") : v;
                    setForm({ ...form, method, pending });
                  }}>
                  <option value="banque">Banque</option>
                  <option value="cash">Cash</option>
                  <option value="attente_banque">En attente banque</option>
                  <option value="attente_cash">En attente cash</option>
                </Select>
              </Field>
            </div>
            <Field label="Libellé"><Input data-testid="ledger-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Ex. Vente gâteaux tournoi" required /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Montant (€)"><Input data-testid="ledger-amount" type="number" step="0.01" min="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required /></Field>
              <Field label="Date"><Input data-testid="ledger-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
            </div>
            <Field label="Catégorie (optionnel)"><Input data-testid="ledger-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Ex. Buvette, Tombola..." /></Field>
            <Button data-testid="save-ledger-btn" className="w-full mt-2">Enregistrer</Button>
          </form>
        )}
      </Modal>
    </div>
  );
}

/* ---------------- Gestion de caisse (buvette) ---------------- */
function Caisse() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canValidate = isAdmin || !!user?.committee_manager;
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const empty = { label: "", date: new Date().toISOString().slice(0, 10), recette_cash: "", recette_carte: "", depense_cash: "", note: "" };

  const load = () => api.get("/committee/caisse").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const num = (v) => Math.max(parseFloat(v) || 0, 0);
  const formTotal = form ? (num(form.recette_cash) + num(form.recette_carte) - num(form.depense_cash)) : 0;

  const save = async (e) => {
    e.preventDefault();
    try {
      if (form.id) await api.put(`/committee/caisse/${form.id}`, form);
      else await api.post("/committee/caisse", form);
      toast.success("Caisse enregistrée"); setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer cette caisse ?")) return;
    try { await api.delete(`/committee/caisse/${id}`); toast.success("Supprimé"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const validate = async (c) => {
    try { const { data } = await api.post(`/committee/caisse/${c.id}/validate`); toast.success(`Validé · ${data.entries} écriture(s) en trésorerie`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const unvalidate = async (c) => {
    if (!window.confirm("Dévalider cette caisse ? Les écritures correspondantes seront retirées de la trésorerie.")) return;
    try { await api.post(`/committee/caisse/${c.id}/unvalidate`); toast.success("Dévalidé"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  return (
    <div data-testid="committee-caisse">
      <Card className="mb-4 border-primary/30">
        <div className="flex items-start gap-2">
          <Coins size={18} className="text-primary shrink-0 mt-0.5" />
          <p className="text-sm text-muted-foreground">
            Saisissez une caisse (buvette…) : recette <b className="text-foreground">cash</b>, recette <b className="text-foreground">carte</b> et dépense cash.
            Après <b className="text-foreground">validation</b> par un administrateur ou un manager, les montants sont reportés en trésorerie
            (cash → recette/dépense cash, carte → recette banque).
          </p>
        </div>
      </Card>

      <div className="flex justify-end mb-4">
        <Button data-testid="add-caisse-btn" onClick={() => { setForm(empty); setOpen(true); }}><Plus size={16} /> Nouvelle caisse</Button>
      </div>

      {list.length ? (
        <div className="space-y-2">
          {list.map((c) => (
            <Card key={c.id} data-testid="caisse-row" className={`py-3 ${c.validated ? "border-primary/30" : ""}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{c.label}</span>
                    {c.validated
                      ? <Badge color="green"><span className="flex items-center gap-1"><BadgeCheck size={12} /> Validé</span></Badge>
                      : <Badge color="yellow"><span className="flex items-center gap-1"><Clock size={12} /> À valider</span></Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{fmtDate(c.date)} · par {c.created_by || "—"}{c.validated && c.validated_by ? ` · validé par ${c.validated_by}` : ""}</p>
                  <div className="flex items-center gap-3 flex-wrap mt-2 text-xs">
                    <span className="inline-flex items-center gap-1 text-green-500 font-semibold"><Coins size={13} /> Cash {euro(c.recette_cash)}</span>
                    <span className="inline-flex items-center gap-1 text-sky-400 font-semibold"><CreditCard size={13} /> Carte {euro(c.recette_carte)}</span>
                    <span className="inline-flex items-center gap-1 text-destructive font-semibold"><TrendingDown size={13} /> Dépense {euro(c.depense_cash)}</span>
                    <span className="inline-flex items-center gap-1 font-bold"><Wallet size={13} /> Total {euro(c.total)}</span>
                  </div>
                  {c.note && <p className="text-xs text-muted-foreground mt-1.5 italic">{c.note}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {canValidate && (c.validated
                    ? <button data-testid={`unvalidate-caisse-${c.id}`} onClick={() => unvalidate(c)} title="Dévalider" className="text-green-500 hover:text-muted-foreground"><Lock size={16} /></button>
                    : <button data-testid={`validate-caisse-${c.id}`} onClick={() => validate(c)} title="Valider et reporter en trésorerie" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-primary/15 text-primary text-xs font-bold hover:bg-primary/25 transition-colors"><BadgeCheck size={14} /> Valider</button>)}
                  {c.can_edit && <button data-testid={`edit-caisse-${c.id}`} onClick={() => { setForm({ ...c, date: (c.date || "").slice(0, 10) }); setOpen(true); }} className="text-muted-foreground hover:text-foreground"><Pencil size={16} /></button>}
                  {(c.can_edit || (canValidate)) && <button data-testid={`delete-caisse-${c.id}`} onClick={() => remove(c.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={16} /></button>}
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : <Empty text="Aucune caisse. Créez une première caisse (buvette, tournoi…)." />}

      <Modal open={open} onClose={() => setOpen(false)} title={form?.id ? "Modifier la caisse" : "Nouvelle caisse"}>
        {form && (
          <form onSubmit={save}>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Libellé"><Input data-testid="caisse-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Ex. Buvette tournoi" /></Field>
              <Field label="Date"><Input data-testid="caisse-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Recette cash (€)"><Input data-testid="caisse-recette-cash" type="number" step="0.01" min="0" value={form.recette_cash} onChange={(e) => setForm({ ...form, recette_cash: e.target.value })} placeholder="0.00" /></Field>
              <Field label="Recette carte (€)"><Input data-testid="caisse-recette-carte" type="number" step="0.01" min="0" value={form.recette_carte} onChange={(e) => setForm({ ...form, recette_carte: e.target.value })} placeholder="0.00" /></Field>
              <Field label="Dépense cash (€)"><Input data-testid="caisse-depense-cash" type="number" step="0.01" min="0" value={form.depense_cash} onChange={(e) => setForm({ ...form, depense_cash: e.target.value })} placeholder="0.00" /></Field>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-secondary/50 border border-border px-3 py-2 mb-3">
              <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Wallet size={14} /> Total</span>
              <span data-testid="caisse-form-total" className={`text-lg font-bold ${formTotal >= 0 ? "text-primary" : "text-destructive"}`}>{euro(formTotal)}</span>
            </div>
            <Field label="Note (optionnel)"><Input data-testid="caisse-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Remarques…" /></Field>
            <Button data-testid="save-caisse-btn" className="w-full mt-1">Enregistrer</Button>
          </form>
        )}
      </Modal>
    </div>
  );
}


/* ---------------- Événements & Tâches ---------------- */
function SortableTask({ task, isAdmin, onCycle, onEdit, onDelete, onToggleDebat, onToggleValide }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id, disabled: !isAdmin });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} data-testid="committee-task" className="rounded-lg bg-secondary/30 px-3 py-2.5">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          {isAdmin && (
            <button data-testid={`task-drag-${task.id}`} {...attributes} {...listeners} title="Glisser pour réordonner"
              className="shrink-0 mt-0.5 -ml-1 text-muted-foreground hover:text-primary cursor-grab active:cursor-grabbing touch-none"><GripVertical size={16} /></button>
          )}
          <div className="min-w-0">
            <p className="text-sm font-semibold break-words">{task.title}</p>
            {task.assignees?.length ? (
              <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                {task.assignees.map((a, i) => (
                  <span key={i} className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[11px] font-bold">
                    <Users size={10} /> {a.name}{!a.user_id && <span className="text-muted-foreground font-normal">(invité)</span>}
                  </span>
                ))}
              </div>
            ) : <p className="text-xs text-muted-foreground mt-1">Non assigné</p>}
            {(task.debat || task.valide) && (
              <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                {task.debat && <span data-testid={`badge-debat-${task.id}`} className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 px-2 py-0.5 text-[11px] font-bold"><MessageSquare size={10} /> À débattre en réunion</span>}
                {task.valide && <span data-testid={`badge-valide-${task.id}`} className="inline-flex items-center gap-1 rounded-full bg-green-500/15 text-green-600 dark:text-green-400 px-2 py-0.5 text-[11px] font-bold"><BadgeCheck size={10} /> Validé en réunion</span>}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isAdmin && (
            <>
              <button data-testid={`toggle-debat-${task.id}`} onClick={onToggleDebat} title="À débattre en réunion"
                className={`transition-colors ${task.debat ? "text-amber-500" : "text-muted-foreground hover:text-amber-500"}`}><MessageSquare size={15} /></button>
              <button data-testid={`toggle-valide-${task.id}`} onClick={onToggleValide} title="Validé en réunion"
                className={`transition-colors ${task.valide ? "text-green-500" : "text-muted-foreground hover:text-green-500"}`}><BadgeCheck size={15} /></button>
            </>
          )}
          <button data-testid={`task-status-${task.id}`} onClick={onCycle} title="Changer le statut">
            <Badge color={STATUS[task.status]?.color || "gray"}>{STATUS[task.status]?.label || task.status}</Badge>
          </button>
          <button data-testid={`edit-task-${task.id}`} onClick={onEdit} className="text-muted-foreground hover:text-foreground"><Pencil size={15} /></button>
          <button data-testid={`delete-task-${task.id}`} onClick={onDelete} className="text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>
        </div>
      </div>
      <CommentsThread targetType="committee_task" targetId={task.id} count={task.comment_count || 0} />
    </div>
  );
}

function Events() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [evOpen, setEvOpen] = useState(false);
  const [evForm, setEvForm] = useState(null);
  const [taskFor, setTaskFor] = useState(null);
  const [taskForm, setTaskForm] = useState(null);
  const [guestName, setGuestName] = useState("");
  const emptyEv = { title: "", date: "", description: "" };
  const emptyTask = { title: "", assignees: [], status: "todo", debat: false, valide: false };

  const load = () => api.get("/committee/events").then((r) => setEvents(r.data));
  useEffect(() => { load(); api.get("/committee/members").then((r) => setMembers(r.data)).catch(() => {}); }, []);

  const saveEvent = async (e) => {
    e.preventDefault();
    try {
      if (evForm.id) await api.put(`/committee/events/${evForm.id}`, evForm);
      else await api.post("/committee/events", evForm);
      toast.success("Événement enregistré"); setEvOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const removeEvent = async (id) => { if (!window.confirm("Supprimer cet événement et ses tâches ?")) return; await api.delete(`/committee/events/${id}`); toast.success("Supprimé"); load(); };

  const openTask = (evId, task) => { setTaskFor(evId); setGuestName(""); setTaskForm(task ? { ...task, assignees: task.assignees || [] } : { ...emptyTask }); };
  const addMember = (id) => {
    if (!id) return;
    const m = members.find((x) => x.id === id);
    if (!m || (taskForm.assignees || []).some((a) => a.user_id === id)) return;
    setTaskForm({ ...taskForm, assignees: [...(taskForm.assignees || []), { user_id: id, name: m.name }] });
  };
  const addGuest = () => {
    const n = guestName.trim();
    if (!n) return;
    setTaskForm({ ...taskForm, assignees: [...(taskForm.assignees || []), { user_id: null, name: n }] });
    setGuestName("");
  };
  const removeAssignee = (idx) => setTaskForm({ ...taskForm, assignees: (taskForm.assignees || []).filter((_, i) => i !== idx) });

  const saveTask = async (e) => {
    e.preventDefault();
    try {
      if (taskForm.id) await api.put(`/committee/tasks/${taskForm.id}`, taskForm);
      else await api.post(`/committee/events/${taskFor}/tasks`, taskForm);
      toast.success("Tâche enregistrée"); setTaskFor(null); setTaskForm(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const removeTask = async (id) => { if (!window.confirm("Supprimer cette tâche ?")) return; await api.delete(`/committee/tasks/${id}`); toast.success("Supprimé"); load(); };
  const cycleStatus = async (t) => {
    const order = ["todo", "in_progress", "done"];
    const next = order[(order.indexOf(t.status) + 1) % 3];
    await api.put(`/committee/tasks/${t.id}`, { status: next }); load();
  };
  const toggleFlag = async (t, key) => {
    await api.put(`/committee/tasks/${t.id}`, { [key]: !t[key] });
    if (key === "debat") toast.success(!t.debat ? "Ajouté à l'ordre du jour de la prochaine réunion" : "Retiré des points à débattre");
    load();
  };
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } })
  );
  const [collapsed, setCollapsed] = useState({});
  const toggleCollapse = (id) => setCollapsed((c) => ({ ...c, [id]: !c[id] }));
  const onDragEnd = async (ev, e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldI = ev.tasks.findIndex((t) => t.id === active.id);
    const newI = ev.tasks.findIndex((t) => t.id === over.id);
    if (oldI < 0 || newI < 0) return;
    const arr = arrayMove(ev.tasks, oldI, newI);
    setEvents((prev) => prev.map((x) => x.id === ev.id ? { ...x, tasks: arr } : x));
    try { await api.put(`/committee/events/${ev.id}/tasks/reorder`, { task_ids: arr.map((t) => t.id) }); }
    catch { load(); }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button data-testid="add-event-btn" onClick={() => { setEvForm(emptyEv); setEvOpen(true); }}><Plus size={16} /> Nouvel événement</Button>
      </div>
      {events.length ? (
        <div className="space-y-4">
          {events.map((ev) => {
            const done = (ev.tasks || []).filter((t) => t.status === "done").length;
            const total = (ev.tasks || []).length;
            const isOpen = !collapsed[ev.id];
            return (
            <Card key={ev.id} data-testid="committee-event" className="border-l-4 border-l-primary overflow-hidden transition-all hover:shadow-lg hover:shadow-primary/5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-11 h-11 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0"><CalendarPlus size={20} /></div>
                  <div className="min-w-0">
                    <h3 className="font-extrabold text-lg leading-tight">{ev.title}</h3>
                    <span className="inline-flex items-center gap-1.5 mt-1 rounded-full bg-secondary px-2.5 py-0.5 text-xs font-bold text-muted-foreground"><Clock size={11} /> {ev.date ? fmtDate(ev.date) : "Date à définir"}</span>
                    {ev.description && <p className="text-sm text-muted-foreground mt-2">{ev.description}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button data-testid={`event-pdf-${ev.id}`} title="Fiche d'organisation (PDF)"
                    onClick={() => openFile(`/committee/events/${ev.id}/pdf`).catch(() => toast.error("PDF indisponible"))}
                    className="inline-flex items-center gap-1 rounded-lg bg-primary/10 text-primary px-2.5 py-1.5 text-xs font-bold hover:bg-primary/20"><FileText size={14} /> PDF</button>
                  <button data-testid={`edit-event-${ev.id}`} onClick={() => { setEvForm({ ...ev, date: (ev.date || "").slice(0, 10) }); setEvOpen(true); }} className="text-muted-foreground hover:text-foreground"><Pencil size={16} /></button>
                  <button data-testid={`delete-event-${ev.id}`} onClick={() => removeEvent(ev.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={16} /></button>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-center justify-between mb-2">
                  <button data-testid={`toggle-tasks-${ev.id}`} onClick={() => toggleCollapse(ev.id)}
                    className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors">
                    <ChevronDown size={16} className={`transition-transform duration-300 ${isOpen ? "" : "-rotate-90"}`} />
                    <ListTodo size={14} /> Tâches
                    {total > 0 && <span className="normal-case tracking-normal rounded-full bg-primary/15 text-primary px-2 py-0.5">{done}/{total}</span>}
                  </button>
                  <Button data-testid={`add-task-${ev.id}`} variant="outline" onClick={() => { setCollapsed((c) => ({ ...c, [ev.id]: false })); openTask(ev.id, null); }}><Plus size={14} /> Tâche</Button>
                </div>
                {isOpen && (
                  ev.tasks?.length ? (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => onDragEnd(ev, e)}>
                      <SortableContext items={ev.tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
                        <div className="space-y-2">
                          {ev.tasks.map((t, ti) => (
                            <div key={t.id} className="animate-in fade-in slide-in-from-top-1 fill-mode-both" style={{ animationDelay: `${ti * 55}ms`, animationDuration: "300ms" }}>
                              <SortableTask task={t} isAdmin={isAdmin}
                                onCycle={() => cycleStatus(t)} onEdit={() => openTask(ev.id, t)} onDelete={() => removeTask(t.id)}
                                onToggleDebat={() => toggleFlag(t, "debat")} onToggleValide={() => toggleFlag(t, "valide")} />
                            </div>
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  ) : <p className="text-xs text-muted-foreground animate-in fade-in duration-300">Aucune tâche pour cet événement.</p>
                )}
              </div>
            </Card>
            );
          })}
        </div>
      ) : <Empty text="Aucun événement. Créez le premier événement du comité." />}

      <Modal open={evOpen} onClose={() => setEvOpen(false)} title={evForm?.id ? "Modifier l'événement" : "Nouvel événement"}>
        {evForm && (
          <form onSubmit={saveEvent}>
            <Field label="Titre"><Input data-testid="event-title" value={evForm.title} onChange={(e) => setEvForm({ ...evForm, title: e.target.value })} required /></Field>
            <Field label="Date"><Input data-testid="event-date" type="date" value={evForm.date} onChange={(e) => setEvForm({ ...evForm, date: e.target.value })} /></Field>
            <Field label="Description"><textarea data-testid="event-description" rows={3} value={evForm.description} onChange={(e) => setEvForm({ ...evForm, description: e.target.value })} className="w-full rounded-lg bg-secondary border border-border p-3 text-sm outline-none focus:border-primary" /></Field>
            <Button data-testid="save-event-btn" className="w-full mt-2">Enregistrer</Button>
          </form>
        )}
      </Modal>

      <Modal open={!!taskForm} onClose={() => { setTaskFor(null); setTaskForm(null); }} title={taskForm?.id ? "Modifier la tâche" : "Nouvelle tâche"}>
        {taskForm && (
          <form onSubmit={saveTask}>
            <Field label="Intitulé de la tâche"><Input data-testid="task-title" value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} placeholder="Ex. Tenir la buvette" required /></Field>

            <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Personnes assignées</label>
            {(taskForm.assignees || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="task-assignees-list">
                {taskForm.assignees.map((a, i) => (
                  <span key={i} className="inline-flex items-center gap-1 rounded-full bg-primary/15 text-primary px-2.5 py-1 text-xs font-bold">
                    {a.name}{!a.user_id && <span className="text-muted-foreground font-normal">(invité)</span>}
                    <button type="button" data-testid={`remove-assignee-${i}`} onClick={() => removeAssignee(i)} className="hover:text-destructive"><X size={12} /></button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2 mb-2">
              <Select data-testid="task-add-member" value="" onChange={(e) => addMember(e.target.value)} className="flex-1">
                <option value="">+ Ajouter un membre du comité</option>
                {members.filter((m) => !(taskForm.assignees || []).some((a) => a.user_id === m.id)).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </Select>
            </div>
            <div className="flex items-center gap-2 mb-4">
              <Input data-testid="task-guest-name" value={guestName} onChange={(e) => setGuestName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addGuest(); } }}
                placeholder="Nom Prénom (personne non-membre)" className="flex-1" />
              <Button type="button" data-testid="task-add-guest" variant="outline" onClick={addGuest}><Plus size={14} /> Ajouter</Button>
            </div>

            <Field label="Statut">
              <Select data-testid="task-status-select" value={taskForm.status} onChange={(e) => setTaskForm({ ...taskForm, status: e.target.value })}>
                <option value="todo">À faire</option>
                <option value="in_progress">En cours</option>
                <option value="done">Terminé</option>
              </Select>
            </Field>
            <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Réunion</label>
            <div className="flex flex-col gap-2 mb-3">
              <button type="button" data-testid="task-form-debat" onClick={() => setTaskForm({ ...taskForm, debat: !taskForm.debat })}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-bold transition-colors ${taskForm.debat ? "border-amber-500 bg-amber-500/10 text-amber-600 dark:text-amber-400" : "border-border text-muted-foreground hover:border-amber-500/40"}`}>
                <MessageSquare size={15} /> À débattre en réunion
                {taskForm.debat && <Check size={14} className="ml-auto" />}
              </button>
              {taskForm.debat && <p className="text-[11px] text-muted-foreground -mt-1">Ce point sera ajouté à l'ordre du jour de la prochaine réunion à venir.</p>}
              <button type="button" data-testid="task-form-valide" onClick={() => setTaskForm({ ...taskForm, valide: !taskForm.valide })}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-bold transition-colors ${taskForm.valide ? "border-green-500 bg-green-500/10 text-green-600 dark:text-green-400" : "border-border text-muted-foreground hover:border-green-500/40"}`}>
                <BadgeCheck size={15} /> Validé en réunion
                {taskForm.valide && <Check size={14} className="ml-auto" />}
              </button>
            </div>
            <Button data-testid="save-task-btn" className="w-full mt-2">Enregistrer</Button>
          </form>
        )}
      </Modal>
    </div>
  );
}

/* ---------------- Boutique ---------------- */
function Shop() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const empty = { name: "", price: "", description: "", stock: 0 };

  const load = () => api.get("/committee/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (form.id) await api.put(`/committee/products/${form.id}`, form);
      else await api.post("/committee/products", form);
      toast.success("Article enregistré"); setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer cet article ?")) return; await api.delete(`/committee/products/${id}`); toast.success("Supprimé"); load(); };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button data-testid="add-product-btn" onClick={() => { setForm(empty); setOpen(true); }}><Plus size={16} /> Nouvel article</Button>
      </div>
      {products.length ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p) => (
            <Card key={p.id} data-testid="committee-product">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-bold">{p.name}</h3>
                <span className="font-black text-primary whitespace-nowrap">{euro(p.price)}</span>
              </div>
              {p.description && <p className="text-sm text-muted-foreground mt-2">{p.description}</p>}
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-border">
                <Badge color={p.stock > 0 ? "green" : "red"}>Stock : {p.stock}</Badge>
                <div className="flex items-center gap-2">
                  <button data-testid={`edit-product-${p.id}`} onClick={() => { setForm({ ...p }); setOpen(true); }} className="text-muted-foreground hover:text-foreground"><Pencil size={16} /></button>
                  <button data-testid={`delete-product-${p.id}`} onClick={() => remove(p.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={16} /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : <Empty text="Aucun article. Ajoutez un article au catalogue du comité." />}

      <Modal open={open} onClose={() => setOpen(false)} title={form?.id ? "Modifier l'article" : "Nouvel article"}>
        {form && (
          <form onSubmit={save}>
            <Field label="Nom de l'article"><Input data-testid="product-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Prix (€)"><Input data-testid="product-price" type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required /></Field>
              <Field label="Stock"><Input data-testid="product-stock" type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} /></Field>
            </div>
            <Field label="Description"><textarea data-testid="product-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full rounded-lg bg-secondary border border-border p-3 text-sm outline-none focus:border-primary" /></Field>
            <Button data-testid="save-product-btn" className="w-full mt-2">Enregistrer</Button>
          </form>
        )}
      </Modal>
    </div>
  );
}

/* ---------------- Documents / PV ---------------- */
function Documents() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/committee/documents").then((r) => setDocs(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choisissez un fichier"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title || file.name);
      await api.post("/committee/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document ajouté"); setOpen(false); setTitle(""); setFile(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Échec de l'envoi"); }
    finally { setBusy(false); }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer ce document ?")) return; await api.delete(`/committee/documents/${id}`); toast.success("Supprimé"); load(); };

  const fmtSize = (b) => { if (!b) return ""; const k = b / 1024; return k < 1024 ? `${k.toFixed(0)} Ko` : `${(k / 1024).toFixed(1)} Mo`; };

  return (
    <div data-testid="committee-documents">
      {isAdmin && (
        <div className="flex justify-end mb-4">
          <Button data-testid="add-document-btn" onClick={() => { setTitle(""); setFile(null); setOpen(true); }}><Upload size={16} /> Ajouter un PV</Button>
        </div>
      )}
      {docs.length ? (
        <div className="space-y-2">
          {docs.map((d) => (
            <Card key={d.id} data-testid="committee-document-row" className="py-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><FileText size={18} className="text-primary" /></div>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">{d.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{d.filename} · {fmtSize(d.size)} · {fmtDate(d.created_at)}{d.uploaded_by ? ` · ${d.uploaded_by}` : ""}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <button data-testid={`open-document-${d.id}`} title="Consulter"
                    onClick={() => openFile(`/committee/documents/${d.id}/download`).catch(() => toast.error("Ouverture échouée"))}
                    className="text-muted-foreground hover:text-primary"><FolderOpen size={17} /></button>
                  <button data-testid={`download-document-${d.id}`} title="Télécharger"
                    onClick={() => downloadFile(`/committee/documents/${d.id}/download`, d.filename || d.title).catch(() => toast.error("Téléchargement échoué"))}
                    className="text-muted-foreground hover:text-foreground"><Download size={17} /></button>
                  {isAdmin && (
                    <button data-testid={`delete-document-${d.id}`} title="Supprimer" onClick={() => remove(d.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={17} /></button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : <Empty text="Aucun document. L'administrateur peut ajouter les PV du comité." />}

      <Modal open={open} onClose={() => setOpen(false)} title="Ajouter un document / PV">
        <form onSubmit={upload}>
          <Field label="Titre (optionnel)"><Input data-testid="document-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex. PV réunion du 12 mars" /></Field>
          <Field label="Fichier (PDF, Word, Excel, image...)">
            <input data-testid="document-file" type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.webp"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm text-muted-foreground file:mr-3 file:rounded-lg file:border-0 file:bg-primary file:text-primary-foreground file:px-4 file:py-2 file:font-bold file:cursor-pointer" />
          </Field>
          <Button data-testid="save-document-btn" className="w-full mt-2" disabled={busy}>{busy ? "Envoi..." : "Envoyer"}</Button>
        </form>
      </Modal>
    </div>
  );
}

/* ---------------- Votes anonymes ---------------- */
const VOTE_OPTS = [
  { k: "pour", label: "Pour", color: "green" },
  { k: "contre", label: "Contre", color: "red" },
  { k: "abstention", label: "Abstention", color: "gray" },
];
function Votes() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [polls, setPolls] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ question: "", description: "" });

  const load = () => api.get("/committee/polls").then((r) => setPolls(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/committee/polls", form);
      toast.success("Vote créé"); setOpen(false); setForm({ question: "", description: "" }); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const vote = async (pid, choice) => {
    try { await api.post(`/committee/polls/${pid}/vote`, { choice }); toast.success("Vote enregistré"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const remove = async (pid) => { if (!window.confirm("Supprimer ce vote ?")) return; await api.delete(`/committee/polls/${pid}`); toast.success("Supprimé"); load(); };

  return (
    <div data-testid="committee-votes">
      {isAdmin && (
        <div className="flex justify-end mb-4">
          <Button data-testid="add-poll-btn" onClick={() => { setForm({ question: "", description: "" }); setOpen(true); }}><Plus size={16} /> Nouvelle question</Button>
        </div>
      )}
      {polls.length ? (
        <div className="space-y-4">
          {polls.map((p) => {
            const total = Object.values(p.counts || {}).reduce((a, b) => a + b, 0);
            return (
              <Card key={p.id} data-testid="committee-poll">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-bold flex items-center gap-2"><Vote size={16} className="text-primary shrink-0" /> {p.question}</h3>
                    {p.description && <p className="text-sm text-muted-foreground mt-1">{p.description}</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-muted-foreground" data-testid={`poll-voters-${p.id}`}><Users size={13} /> {p.total_votes} votant{p.total_votes > 1 ? "s" : ""}{p.eligible_voters ? ` / ${p.eligible_voters}` : ""}</span>
                    {isAdmin && <button data-testid={`delete-poll-${p.id}`} onClick={() => remove(p.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={16} /></button>}
                  </div>
                </div>

                {p.has_voted && (
                  <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-green-500"><Check size={14} /> Vous avez voté (anonyme)</div>
                )}

                {!p.has_voted ? (
                  <div className="mt-4 flex flex-wrap gap-2" data-testid={`poll-vote-buttons-${p.id}`}>
                    {VOTE_OPTS.map((o) => (
                      <button key={o.k} data-testid={`poll-vote-${p.id}-${o.k}`} onClick={() => vote(p.id, o.k)}
                        className={`px-4 py-2 rounded-lg text-sm font-bold border transition-colors ${o.color === "green" ? "border-green-500/40 text-green-500 hover:bg-green-500/10" : o.color === "red" ? "border-destructive/40 text-destructive hover:bg-destructive/10" : "border-border text-muted-foreground hover:bg-secondary"}`}>
                        {o.label}
                      </button>
                    ))}
                  </div>
                ) : p.show_results ? (
                  <div className="mt-4 space-y-2" data-testid={`poll-results-${p.id}`}>
                    {VOTE_OPTS.map((o) => {
                      const c = (p.counts || {})[o.k] || 0;
                      const pct = total ? Math.round((c / total) * 100) : 0;
                      return (
                        <div key={o.k}>
                          <div className="flex items-center justify-between text-xs font-bold mb-1">
                            <span>{o.label}</span><span className="text-muted-foreground">{c} · {pct}%</span>
                          </div>
                          <div className="h-2.5 rounded-full bg-secondary overflow-hidden">
                            <div className={`h-full rounded-full ${o.color === "green" ? "bg-green-500" : o.color === "red" ? "bg-destructive" : "bg-muted-foreground"}`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="mt-4 text-xs text-muted-foreground italic">Les résultats seront visibles après votre vote.</p>
                )}
              </Card>
            );
          })}
        </div>
      ) : <Empty text="Aucun vote en cours. L'administrateur peut créer une question de vote." />}

      <Modal open={open} onClose={() => setOpen(false)} title="Nouvelle question de vote">
        <form onSubmit={create}>
          <Field label="Question"><Input data-testid="poll-question" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} placeholder="Ex. Approuvez-vous le budget du tournoi ?" required /></Field>
          <Field label="Description (optionnel)"><textarea data-testid="poll-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full rounded-lg bg-secondary border border-border p-3 text-sm outline-none focus:border-primary" /></Field>
          <p className="text-xs text-muted-foreground mb-2">Options de réponse : <b>Pour</b> / <b>Contre</b> / <b>Abstention</b>. Chaque membre du comité vote une seule fois, de manière anonyme.</p>
          <Button data-testid="save-poll-btn" className="w-full mt-1">Créer le vote</Button>
        </form>
      </Modal>
    </div>
  );
}

/* ---------------- Réunions du comité ---------------- */
function fmtMeetingDT(dt) {
  if (!dt) return "";
  try {
    const d = new Date(dt);
    return d.toLocaleString("fr-FR", { weekday: "long", day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return dt; }
}
function Meetings({ onGoToEvents }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [meetings, setMeetings] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const empty = { title: "", datetime: "", location: "", agenda: "" };

  const load = () => api.get("/committee/meetings").then((r) => setMeetings(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (form.id) { await api.put(`/committee/meetings/${form.id}`, form); toast.success("Réunion mise à jour"); }
      else { const { data } = await api.post("/committee/meetings", form); toast.success(`Convocation envoyée à ${data.invited} membre(s)`); }
      setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer cette réunion ?")) return; await api.delete(`/committee/meetings/${id}`); toast.success("Supprimé"); load(); };
  const remind = async (id) => { const { data } = await api.post(`/committee/meetings/${id}/remind`); toast.success(data.reminded ? `Rappel envoyé à ${data.reminded} membre(s)` : "Tout le monde a déjà répondu"); };
  const respond = async (id, status, proxy_to = null) => {
    try { await api.post(`/committee/meetings/${id}/respond`, { status, proxy_to }); toast.success("Réponse enregistrée"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const addSuggestion = async (id, text) => {
    try { await api.post(`/committee/meetings/${id}/suggestions`, { text }); toast.success("Suggestion ajoutée"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const deleteSuggestion = async (id, sid) => {
    try { await api.delete(`/committee/meetings/${id}/suggestions/${sid}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const promoteSuggestion = async (id, sid) => {
    try { await api.post(`/committee/meetings/${id}/suggestions/${sid}/promote`); toast.success("Ajouté à l'ordre du jour"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const likeSuggestion = async (id, sid) => {
    try { await api.post(`/committee/meetings/${id}/suggestions/${sid}/like`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  return (
    <div data-testid="committee-meetings">
      {isAdmin && (
        <div className="flex justify-end mb-4">
          <Button data-testid="add-meeting-btn" onClick={() => { setForm(empty); setOpen(true); }}><Plus size={16} /> Nouvelle réunion</Button>
        </div>
      )}

      <MeetingPoints meetings={meetings} isAdmin={isAdmin} onInserted={load} />

      {meetings.length ? (
        <div className="space-y-4">
          {meetings.map((m) => <MeetingCard key={m.id} m={m} isAdmin={isAdmin} onGoToEvents={onGoToEvents} onEdit={() => { setForm({ ...m, datetime: (m.datetime || "").slice(0, 16) }); setOpen(true); }} onDelete={() => remove(m.id)} onRemind={() => remind(m.id)} onRespond={respond} onAddSuggestion={addSuggestion} onDeleteSuggestion={deleteSuggestion} onPromoteSuggestion={promoteSuggestion} onLikeSuggestion={likeSuggestion} />)}
        </div>
      ) : <Empty text="Aucune réunion planifiée. L'administrateur peut créer une réunion et convoquer le comité." />}

      <Modal open={open} onClose={() => setOpen(false)} title={form?.id ? "Modifier la réunion" : "Nouvelle réunion"}>
        {form && (
          <form onSubmit={save}>
            <Field label="Titre / objet"><Input data-testid="meeting-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex. Réunion mensuelle du comité" required /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date et heure"><Input data-testid="meeting-datetime" type="datetime-local" value={form.datetime} onChange={(e) => setForm({ ...form, datetime: e.target.value })} required /></Field>
              <Field label="Lieu"><Input data-testid="meeting-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Ex. Buvette du club" /></Field>
            </div>
            <Field label="Ordre du jour"><textarea data-testid="meeting-agenda" rows={5} value={form.agenda} onChange={(e) => setForm({ ...form, agenda: e.target.value })} placeholder={"1. Bilan financier\n2. Organisation du tournoi\n3. Divers"} className="w-full rounded-lg bg-secondary border border-border p-3 text-sm outline-none focus:border-primary" /></Field>
            {!form.id && <p className="text-xs text-muted-foreground mb-2">Une convocation (notification + push) sera envoyée à tous les membres du comité.</p>}
            <Button data-testid="save-meeting-btn" className="w-full mt-1">{form.id ? "Enregistrer" : "Créer et convoquer"}</Button>
          </form>
        )}
      </Modal>
    </div>
  );
}

function MeetingPoints({ meetings, isAdmin, onInserted }) {
  const [points, setPoints] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "" });
  const [insertFor, setInsertFor] = useState(null);
  const load = () => api.get("/committee/meeting-points").then((r) => setPoints(r.data));
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault(); if (!form.title.trim()) return;
    try { await api.post("/committee/meeting-points", form); toast.success("Point proposé"); setForm({ title: "", description: "" }); setOpen(false); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const like = async (id) => { try { await api.post(`/committee/meeting-points/${id}/like`); load(); } catch { /* ignore */ } };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ce point ?")) return;
    try { await api.delete(`/committee/meeting-points/${id}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const insert = async (id, meetingId) => {
    try { await api.post(`/committee/meeting-points/${id}/insert`, { meeting_id: meetingId }); toast.success("Point inséré à la réunion ✔"); setInsertFor(null); load(); onInserted && onInserted(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const nowIso = new Date().toISOString();
  const upcoming = (meetings || []).filter((m) => (m.datetime || "") >= nowIso);
  return (
    <Card className="mb-6 border-primary/30" data-testid="meeting-points">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="min-w-0">
          <h3 className="font-bold flex items-center gap-2"><ListTodo size={16} className="text-primary shrink-0" /> Points de réunion</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Sujets à débattre (sans date). L'administrateur les valide et les insère dans une réunion.</p>
        </div>
        <Button data-testid="add-meeting-point-btn" variant="outline" onClick={() => setOpen(true)}><Plus size={15} /> Proposer un point</Button>
      </div>
      {points.length ? (
        <div className="space-y-2">
          {points.map((p) => (
            <div key={p.id} data-testid="meeting-point-row" className={`rounded-lg px-3 py-2.5 ${p.status === "inserted" ? "bg-primary/5 border border-primary/20" : "bg-secondary/30"}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold break-words">{p.title}</p>
                  {p.description && <p className="text-xs text-muted-foreground mt-0.5 whitespace-pre-wrap">{p.description}</p>}
                  <p className="text-[11px] text-muted-foreground mt-1">Proposé par {p.author_name || "—"}
                    {p.status === "inserted" && <span className="ml-1.5 inline-flex items-center gap-1 text-primary font-bold"><BadgeCheck size={12} /> Inséré{p.meeting_title ? ` · ${p.meeting_title}` : ""}</span>}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button data-testid={`like-point-${p.id}`} onClick={() => like(p.id)} title="Voter pour ce point"
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold border transition-colors ${p.liked ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-primary hover:border-primary/40"}`}><ThumbsUp size={13} /> {p.like_count}</button>
                  {isAdmin && p.status !== "inserted" && <button data-testid={`insert-point-${p.id}`} onClick={() => setInsertFor(insertFor === p.id ? null : p.id)} title="Insérer dans une réunion" className="text-muted-foreground hover:text-primary"><CalendarPlus size={16} /></button>}
                  {p.can_delete && <button data-testid={`delete-point-${p.id}`} onClick={() => remove(p.id)} title="Supprimer" className="text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>}
                </div>
              </div>
              {isAdmin && insertFor === p.id && (
                <div className="mt-2 flex items-center gap-2 flex-wrap border-t border-border pt-2" data-testid={`insert-panel-${p.id}`}>
                  {upcoming.length ? (
                    <>
                      <span className="text-xs text-muted-foreground font-semibold">Insérer dans :</span>
                      {upcoming.map((m) => (
                        <button key={m.id} data-testid={`insert-point-${p.id}-meeting-${m.id}`} onClick={() => insert(p.id, m.id)}
                          className="px-2.5 py-1 rounded-full bg-primary/15 text-primary text-xs font-bold hover:bg-primary/25 transition-colors">{m.title}</button>
                      ))}
                    </>
                  ) : <span className="text-xs text-muted-foreground">Aucune réunion à venir. Créez d'abord une réunion.</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : <p className="text-xs text-muted-foreground">Aucun point pour l'instant. Proposez un sujet à débattre en réunion.</p>}

      <Modal open={open} onClose={() => setOpen(false)} title="Proposer un point de réunion">
        <form onSubmit={create}>
          <Field label="Sujet"><Input data-testid="point-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex. Achat de nouveaux ballons" required /></Field>
          <Field label="Détails (optionnel)"><textarea data-testid="point-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full rounded-lg bg-secondary border border-border p-3 text-sm outline-none focus:border-primary" /></Field>
          <p className="text-xs text-muted-foreground mb-2">Ce sujet restera dans la liste jusqu'à ce qu'un administrateur l'insère dans une réunion.</p>
          <Button data-testid="save-point-btn" className="w-full mt-1">Proposer le point</Button>
        </form>
      </Modal>
    </Card>
  );
}


function MeetingCard({ m, isAdmin, onGoToEvents, onEdit, onDelete, onRemind, onRespond, onAddSuggestion, onDeleteSuggestion, onPromoteSuggestion, onLikeSuggestion }) {
  const [proxy, setProxy] = useState(m.my_proxy_to || "");
  const [sugText, setSugText] = useState("");
  const s = m.summary || {};
  const others = m.present_members.filter((p) => p.id !== null);
  const debatTasks = m.debat_tasks || [];
  const submitSug = (e) => { e.preventDefault(); if (!sugText.trim()) return; onAddSuggestion(m.id, sugText.trim()); setSugText(""); };
  return (
    <Card data-testid="committee-meeting" className={m.is_next ? "border-l-4 border-l-primary" : ""}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-bold flex items-center gap-2 flex-wrap"><CalendarClock size={16} className="text-primary shrink-0" /> {m.title}
            {m.is_next && <span className="inline-flex items-center rounded-full bg-primary/15 text-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">Prochaine réunion</span>}
          </h3>
          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5 flex-wrap">
            <Clock size={12} /> {fmtMeetingDT(m.datetime)}
            {m.location && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {m.location}</span>}
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2 shrink-0">
            <button data-testid={`remind-meeting-${m.id}`} onClick={onRemind} title="Relancer les non-répondants" className="text-muted-foreground hover:text-primary"><Send size={16} /></button>
            <button data-testid={`edit-meeting-${m.id}`} onClick={onEdit} className="text-muted-foreground hover:text-foreground"><Pencil size={16} /></button>
            <button data-testid={`delete-meeting-${m.id}`} onClick={onDelete} className="text-muted-foreground hover:text-destructive"><Trash2 size={16} /></button>
          </div>
        )}
      </div>

      {(m.agenda || (m.is_next && debatTasks.length > 0)) && (
        <div className="mt-3 rounded-lg bg-secondary/40 p-3">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1">Ordre du jour</p>
          {m.agenda && <p className="text-sm whitespace-pre-wrap">{m.agenda}</p>}
          {m.is_next && debatTasks.length > 0 && (
            <div className={m.agenda ? "mt-3 pt-3 border-t border-border" : ""} data-testid={`meeting-debat-tasks-${m.id}`}>
              <p className="text-[10px] uppercase tracking-widest text-amber-600 dark:text-amber-400 font-bold mb-1.5 flex items-center gap-1"><MessageSquare size={11} /> Points issus des événements à débattre</p>
              <div className="space-y-1.5">
                {debatTasks.map((dt) => (
                  <button key={dt.task_id} data-testid={`debat-task-${dt.task_id}`} onClick={onGoToEvents}
                    className="w-full flex items-center justify-between gap-2 rounded-md bg-background/60 hover:bg-background px-2.5 py-1.5 text-left transition-colors group">
                    <span className="text-sm truncate">{dt.title}
                      {dt.valide && <span className="ml-2 inline-flex items-center gap-0.5 rounded-full bg-green-500/15 text-green-600 dark:text-green-400 px-1.5 py-0.5 text-[10px] font-bold"><BadgeCheck size={9} /> Validé</span>}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground shrink-0 group-hover:text-primary">
                      <CalendarPlus size={12} /> {dt.event_title || "Événement"} <ExternalLink size={11} />
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mt-3 flex items-center gap-2 flex-wrap text-xs font-bold">
        <Badge color="green">{s.present || 0} présent{(s.present || 0) > 1 ? "s" : ""}</Badge>
        <Badge color="red">{s.absent || 0} absent{(s.absent || 0) > 1 ? "s" : ""}</Badge>
        <Badge color="yellow">{s.pending || 0} en attente</Badge>
        <span className="inline-flex items-center gap-1 text-muted-foreground" data-testid={`meeting-responded-${m.id}`}><Users size={13} /> {s.responded || 0}/{s.total || 0} ont répondu</span>
      </div>

      {m.is_invited && (
        <div className="mt-4 pt-4 border-t border-border" data-testid={`meeting-respond-${m.id}`}>
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Votre réponse</p>
          <div className="flex flex-wrap items-center gap-2">
            <button data-testid={`meeting-present-${m.id}`} onClick={() => onRespond(m.id, "present", null)}
              className={`px-4 py-2 rounded-lg text-sm font-bold border transition-colors inline-flex items-center gap-1.5 ${m.my_status === "present" ? "bg-green-500 text-white border-green-500" : "border-green-500/40 text-green-500 hover:bg-green-500/10"}`}><Check size={15} /> Présent</button>
            <button data-testid={`meeting-absent-${m.id}`} onClick={() => onRespond(m.id, "absent", proxy || null)}
              className={`px-4 py-2 rounded-lg text-sm font-bold border transition-colors inline-flex items-center gap-1.5 ${m.my_status === "absent" ? "bg-destructive text-white border-destructive" : "border-destructive/40 text-destructive hover:bg-destructive/10"}`}><X size={15} /> Absent</button>
            {m.my_status === "absent" && (
              <div className="inline-flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Procuration à</span>
                <Select data-testid={`meeting-proxy-${m.id}`} value={proxy} onChange={(e) => { setProxy(e.target.value); onRespond(m.id, "absent", e.target.value || null); }} className="w-48">
                  <option value="">— Personne —</option>
                  {others.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </Select>
              </div>
            )}
          </div>
          {m.my_status === "absent" && others.length === 0 && <p className="text-[11px] text-muted-foreground mt-2">Aucun participant présent pour l'instant : la procuration ne peut être donnée qu'à un membre ayant répondu « Présent ».</p>}
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-border">
        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Participants ({m.participants.length})</p>
        <div className="space-y-1.5">
          {m.participants.map((p) => (
            <div key={p.user_id} data-testid={`meeting-participant-${m.id}-${p.user_id}`} className="flex items-center justify-between gap-2 text-sm">
              <span className="truncate">{p.name}</span>
              <div className="flex items-center gap-2 shrink-0">
                {p.proxy_to_name && <span className="text-xs text-muted-foreground italic">→ procuration à {p.proxy_to_name}</span>}
                <Badge color={p.status === "present" ? "green" : p.status === "absent" ? "red" : "yellow"}>
                  {p.status === "present" ? "Présent" : p.status === "absent" ? "Absent" : "En attente"}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-border" data-testid={`meeting-suggestions-${m.id}`}>
        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5"><ListTodo size={14} /> Suggestions de points à débattre ({m.suggestions?.length || 0})</p>
        {m.suggestions?.length ? (
          <div className="space-y-1.5 mb-3">
            {m.suggestions.map((sg) => (
              <div key={sg.id} data-testid={`meeting-suggestion-${sg.id}`} className="flex items-start justify-between gap-2 rounded-lg bg-secondary/30 px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm break-words">{sg.text}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">proposé par {sg.author_name}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button data-testid={`like-suggestion-${sg.id}`} onClick={() => onLikeSuggestion(m.id, sg.id)} title="Voter pour ce point"
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold border transition-colors ${sg.liked ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-primary hover:border-primary/40"}`}>
                    <ThumbsUp size={13} /> {sg.like_count || 0}
                  </button>
                  {isAdmin && (
                    <button data-testid={`promote-suggestion-${sg.id}`} onClick={() => onPromoteSuggestion(m.id, sg.id)} title="Ajouter à l'ordre du jour" className="text-muted-foreground hover:text-primary"><Check size={15} /></button>
                  )}
                  {sg.can_delete && (
                    <button data-testid={`delete-suggestion-${sg.id}`} onClick={() => onDeleteSuggestion(m.id, sg.id)} title="Supprimer" className="text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : <p className="text-xs text-muted-foreground mb-3">Aucune suggestion pour l'instant. Proposez un point à ajouter à l'ordre du jour.</p>}
        <form onSubmit={submitSug} className="flex items-center gap-2">
          <Input data-testid={`suggestion-input-${m.id}`} value={sugText} onChange={(e) => setSugText(e.target.value)} placeholder="Proposer un point à débattre…" className="flex-1" />
          <Button data-testid={`suggestion-submit-${m.id}`} type="submit" variant="outline"><Plus size={14} /> Proposer</Button>
        </form>
      </div>
    </Card>
  );
}


/* ---------------- Membres & Managers ---------------- */
function MembersAdmin() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [members, setMembers] = useState([]);
  const load = () => api.get("/committee/members").then((r) => setMembers(r.data));
  useEffect(() => { load(); }, []);

  const toggle = async (m) => {
    try {
      await api.patch(`/admin/users/${m.id}/committee-manager`, { committee_manager: !m.committee_manager });
      toast.success(m.committee_manager ? "Manager retiré" : "Manager nommé ✔"); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  return (
    <div data-testid="committee-members">
      <Card className="mb-4 border-primary/30">
        <div className="flex items-start gap-2">
          <ShieldCheck size={18} className="text-primary shrink-0 mt-0.5" />
          <p className="text-sm text-muted-foreground">
            Les <b className="text-foreground">Managers</b> (et les administrateurs) peuvent <b className="text-foreground">confirmer / verrouiller</b> les entrées de trésorerie.
            Une entrée confirmée ne peut plus être modifiée ni supprimée, sauf par un administrateur ou un manager.
          </p>
        </div>
      </Card>
      {members.length ? (
        <div className="space-y-2">
          {members.map((m) => (
            <Card key={m.id} data-testid="committee-member-row" className="py-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm truncate">{m.name || m.email}</span>
                    {m.is_admin && <Badge color="red">Admin</Badge>}
                    {m.committee_manager && <Badge color="purple"><span className="flex items-center gap-1"><ShieldCheck size={12} /> Manager</span></Badge>}
                  </div>
                  {m.email && <p className="text-xs text-muted-foreground mt-1 truncate">{m.email}</p>}
                </div>
                {isAdmin && !m.is_admin && (
                  <Button data-testid={`toggle-manager-${m.id}`} variant={m.committee_manager ? "ghost" : "outline"} onClick={() => toggle(m)}>
                    <ShieldCheck size={15} /> {m.committee_manager ? "Retirer Manager" : "Nommer Manager"}
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      ) : <Empty text="Aucun membre du comité pour l'instant." />}
    </div>
  );
}
